<template>
  <div
    v-if="user"
    class="detail-backdrop"
    data-test="user-detail"
    @click.self="emit('close')"
  >
    <section class="detail-card" role="dialog" aria-modal="true" :aria-labelledby="`user-detail-title-${user.id}`">
      <header class="detail-header">
        <div>
          <span>用户 ID：{{ user.id }}</span>
          <h2 :id="`user-detail-title-${user.id}`">{{ user.username }}</h2>
        </div>
        <button type="button" class="btn-close" aria-label="关闭用户详情" @click="emit('close')"></button>
      </header>

      <div class="detail-grid">
        <div><span>姓名</span><strong>{{ fullName }}</strong></div>
        <div><span>角色</span><strong>{{ roleLabel }}</strong></div>
        <div><span>账户状态</span><strong>{{ user.isActive ? '启用' : '禁用' }}</strong></div>
        <div><span>注册时间</span><strong>{{ formatTime(user.createdAt) }}</strong></div>
        <div><span>邮箱</span><strong>{{ user.email || '-' }}</strong></div>
        <div><span>手机号</span><strong>{{ user.phone || '-' }}</strong></div>
        <div><span>累计刷题数</span><strong>{{ formatNumber(user.activityStats?.totalQuestions || 0) }}</strong></div>
        <div><span>正确率</span><strong>{{ user.activityStats?.correctRate || 0 }}%</strong></div>
        <div><span>当前错题数</span><strong>{{ formatNumber(user.activityStats?.wrongQuestionCount || 0) }}</strong></div>
        <div><span>完成会话数</span><strong>{{ formatNumber(user.activityStats?.totalSessions || 0) }}</strong></div>
        <div><span>最后活跃</span><strong>{{ formatTime(user.activityStats?.lastActiveTime) }}</strong></div>
        <div><span>直接邀请人数</span><strong>{{ formatNumber(user.referralStats?.invitedCount || 0) }}</strong></div>
        <div><span>已达标邀请</span><strong>{{ formatNumber(user.referralStats?.qualifiedCount || 0) }}</strong></div>
        <div><span>待达标邀请</span><strong>{{ formatNumber(user.referralStats?.pendingCount || 0) }}</strong></div>
        <div><span>已拒绝邀请</span><strong>{{ formatNumber(user.referralStats?.rejectedCount || 0) }}</strong></div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { UserWithActivity } from '@/types/user-statistics'

const props = defineProps<{ user: UserWithActivity | null }>()
const emit = defineEmits<{ close: [] }>()

const fullName = computed(() => {
  const value = `${props.user?.firstName || ''} ${props.user?.lastName || ''}`.trim()
  return value || '-'
})
const roleLabel = computed(() => {
  const role = typeof props.user?.role === 'string' ? props.user.role : props.user?.role?.name
  return role === 'admin' ? '管理员' : role === 'user' ? '普通用户' : role || '未知角色'
})
const formatNumber = (value: number) => new Intl.NumberFormat('zh-CN').format(value)
const formatTime = (value: string | Date | null | undefined) => value
  ? new Date(value).toLocaleString('zh-CN', { hour12: false })
  : '-'
</script>

<style scoped>
.detail-backdrop {
  position: fixed;
  z-index: 1050;
  display: grid;
  inset: 0;
  place-items: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.55);
}

.detail-card {
  width: min(46rem, 100%);
  max-height: calc(100vh - 2rem);
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
}

.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.detail-header span {
  color: #2563eb;
  font-size: 0.78rem;
  font-weight: 700;
}

.detail-header h2 {
  margin: 0.2rem 0 0;
  font-size: 1.35rem;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1px;
  padding: 1px;
  background: #e2e8f0;
}

.detail-grid div {
  min-width: 0;
  padding: 0.9rem 1.1rem;
  background: #fff;
}

.detail-grid span,
.detail-grid strong {
  display: block;
}

.detail-grid span {
  margin-bottom: 0.25rem;
  color: #64748b;
  font-size: 0.75rem;
}

.detail-grid strong {
  overflow-wrap: anywhere;
  color: #0f172a;
  font-size: 0.9rem;
}

@media (max-width: 576px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
